import {
  BUILT_ONBOARDING_CHAIN,
  type OnboardingChainStep,
  previousBuiltStep,
} from '@/types/onboarding-chain'

import { BRAND_FACTS_NAVIGATOR } from './brand-facts-navigation'
import { COMPETITORS_NAVIGATOR } from './competitors-navigation'
import { CONTENT_STRATEGY_NAVIGATOR } from './content-strategy-navigation'
import { CUSTOMER_PROFILE_NAVIGATOR } from './customer-profile-navigation'
import { VISUAL_STYLE_NAVIGATOR } from './visual-style-navigation'

export interface SetupScreen<Id extends string = string> {
  /** Names the screen within its step. */
  readonly id: Id
  /** True for a screen that shows no Back and that Back passes over. */
  readonly transient: boolean
}

/** A step's screens, in order, and the moves between them. */
export interface SetupStepNavigator<Screen extends string, Move extends string> {
  /** The step's screens. The index of a screen is its place in setup. */
  readonly screens: readonly SetupScreen<Screen>[]
  /** The screen a move leads to. A move that does not apply to the screen on show keeps it. */
  move(from: Screen, move: Move): Screen
}

/** The index of a screen in its step's list, or `-1` when the list does not hold it. */
export function screenIndex<Screen extends string>(
  screens: readonly SetupScreen<Screen>[],
  screen: Screen,
): number {
  return screens.findIndex((candidate) => candidate.id === screen)
}

/** The screen at an index of a step's list, or `null` for an index out of range. */
export function screenAt<Screen extends string>(
  screens: readonly SetupScreen<Screen>[],
  index: number,
): Screen | null {
  return screens[index]?.id ?? null
}

/** Where the founder is in setup: a step and the index of a screen in it. */
export interface SetupPosition {
  /** The step on show. */
  readonly step: OnboardingChainStep
  /** The index of the screen on show, in the step's screen list. */
  readonly index: number
}

/** Where Continue on a step's last screen leads: the next step, or out of setup. */
export type SetupForward = SetupPosition | 'finish'

/** Each step's screens, in order. A step that has not shipped has none. */
export type SetupStepScreens = Readonly<Record<OnboardingChainStep, readonly SetupScreen[]>>

/** The screens of every step. A new step registers its navigator's screens here. */
export const SETUP_STEP_SCREENS: SetupStepScreens = {
  'brand-facts': BRAND_FACTS_NAVIGATOR.screens,
  competitors: COMPETITORS_NAVIGATOR.screens,
  'customer-profile': CUSTOMER_PROFILE_NAVIGATOR.screens,
  strategy: CONTENT_STRATEGY_NAVIGATOR.screens,
  'visual-style': VISUAL_STYLE_NAVIGATOR.screens,
}

/** The index of the last screen of a step that Back may land on, or `null` when it has none. */
export function lastBackIndex(
  step: OnboardingChainStep,
  screens: SetupStepScreens = SETUP_STEP_SCREENS,
): number | null {
  const list = screens[step]
  for (let index = list.length - 1; index >= 0; index -= 1) {
    if (list[index]?.transient === false) return index
  }
  return null
}

/** Where Back leads: the previous non-transient screen of the step, else the last such screen of the shipped step before; `null` on a transient or first screen. */
export function backTarget(
  position: SetupPosition,
  built: readonly OnboardingChainStep[] = BUILT_ONBOARDING_CHAIN,
  screens: SetupStepScreens = SETUP_STEP_SCREENS,
): SetupPosition | null {
  const list = screens[position.step]
  if (list[position.index]?.transient !== false) return null
  for (let index = position.index - 1; index >= 0; index -= 1) {
    if (list[index]?.transient === false) return { step: position.step, index }
  }
  const previous = previousBuiltStep(position.step, built)
  if (previous === null) return null
  const index = lastBackIndex(previous, screens)
  return index === null ? null : { step: previous, index }
}

/** Where Continue on a step's last screen leads: the first screen of the next shipped step, or `'finish'`. */
export function nextStepTarget(
  step: OnboardingChainStep,
  built: readonly OnboardingChainStep[] = BUILT_ONBOARDING_CHAIN,
): SetupForward {
  const at = built.indexOf(step)
  const next = at < 0 ? undefined : built[at + 1]
  return next === undefined ? 'finish' : { step: next, index: 0 }
}
